import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button } from '@garnish/core';
import { TextField } from '@garnish/forms';

import type { CreditSectionProps } from '../CreditSectionProps';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditOverviewPromoCodeForm = (props: CreditOverviewPromoCodeFormProps) => {
  const {
    buttonPlacement = 'right',
    onPressApplyPromoCode,
    hasPromoCodeError,
    isPromoCodeApplied,
    promoCode,
    isSubmittingPromoCode,
    setPromoCode,
    handleShowScanner,
  } = props;

  const onPress = React.useCallback(() => {
    onPressApplyPromoCode(promoCode);
  }, [promoCode, onPressApplyPromoCode]);

  const shouldDisableApplyBtn = promoCode.trim() === '';
  const { formatMessage } = useIntl();
  const isBottomButton = buttonPlacement === 'bottom';
  const cameraIcon = isBottomButton ? 'IconCamera' : undefined;
  const inputPadding = isBottomButton ? undefined : styles.inputPadding;

  const buttonA11yLabel = formatMessage(messages.accountCreditApplyPromoCodeBtnA11yLabel);

  const buttonProps: React.ComponentProps<typeof Button> = {
    testID: 'account.promo-code-button',
    palette: 'primary',
    accessibilityLabel: buttonA11yLabel,
    accessibilityHint: buttonA11yLabel,
    accessibilityRole: 'button',
    accessibilityState: { disabled: shouldDisableApplyBtn },
    disabled: shouldDisableApplyBtn,
    isLoading: isSubmittingPromoCode,
    onPress,
  };

  const promoCodeAlreadyAppliedNoticeText = formatMessage(
    messages.accountCreditPromoCodeAlreadyAppliedNotice,
  );
  const promoCodeAlreadyAppliedNotice = isPromoCodeApplied
    ? promoCodeAlreadyAppliedNoticeText
    : undefined;

  const invalidPromoCodeNoticeText = formatMessage(messages.accountCreditPromoCodeInvalidNotice);
  const invalidPromoCodeNotice = hasPromoCodeError ? invalidPromoCodeNoticeText : undefined;

  const placeholder = formatMessage(messages.accountCreditFieldPromoCodePlaceholder);
  const helperText = formatMessage(messages.accountCreditPromoHelperTextPromoCode);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      <View style={styles.promoCodeWrapper}>
        <View style={[styles.promoCodeInputWrapper, inputPadding]}>
          <TextField
            testID="account.promo-code-field"
            placeholder={placeholder}
            accessibilityLabel={placeholder}
            value={promoCode}
            style={styles.field}
            fieldOptionIcon={cameraIcon}
            fieldOptionOnPress={handleShowScanner}
            onChangeText={setPromoCode}
            onSubmit={onPress}
            notice={promoCodeAlreadyAppliedNotice ?? invalidPromoCodeNotice}
            invalid={hasPromoCodeError}
            noticePalette="caution"
          />
        </View>

        {!isBottomButton && (
          <Button width={125} {...buttonProps}>
            {formatMessage(messages.creditPromoCodeApply)}
          </Button>
        )}
      </View>

      <BodyText sizeMatch={['14']}>{helperText}</BodyText>

      {isBottomButton ? (
        <Button size="large" style={styles.buttonMargin} {...buttonProps}>
          {formatMessage(messages.creditPromoCodeApply)}
        </Button>
      ) : null}
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountCreditApplyPromoCodeBtnA11yLabel: {
    defaultMessage: 'Apply promo code',
    description: 'Account | Credit | Apply Promo Code Btn A11y Label',
  },
  accountCreditFieldPromoCodePlaceholder: {
    defaultMessage: 'Promo code',
    description: 'Account | Credit | Field Promo Code Placeholder',
  },
  accountCreditPromoHelperTextPromoCode: {
    defaultMessage:
      'Some promo codes will generate new rewards that can only be applied to an active order in your bag.',
    description: 'Account | Credit Promo | Helper Text Promo Code',
  },
  accountCreditPromoCodeAlreadyAppliedNotice: {
    defaultMessage: 'This code has already been applied',
    description: 'Credit Overview Promo Code Form | Notice | Already Applied',
  },
  accountCreditPromoCodeInvalidNotice: {
    defaultMessage: 'This code is not valid.',
    description: 'Credit Overview Promo Code Form | Notice | Invalid',
  },
  creditPromoCodeApply: {
    defaultMessage: 'Apply',
    description: 'Credit | Promo Code Form | Apply',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

export type CreditOverviewPromoCodeFormProps = Readonly<{
  buttonPlacement?: 'right' | 'bottom';
  handleShowScanner?: () => void;
}> &
  Pick<
    CreditSectionProps,
    | 'onPressApplyPromoCode'
    | 'hasPromoCodeError'
    | 'isPromoCodeApplied'
    | 'promoCode'
    | 'setPromoCode'
    | 'isSubmittingPromoCode'
  >;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  promoCodeWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['4'],
  },
  promoCodeInputWrapper: {
    flexGrow: 1,
    marginTop: theme.spacing['6'],
  },
  inputPadding: {
    paddingRight: theme.spacing['8'],
  },
  buttonMargin: {
    marginTop: theme.spacing['10'],
  },
  field: {
    minHeight: 48,
  },
});
